import { TasksActions, toggleTaskStatusActionTypes, UpdateTaskActions, UpdateTaskAdditionalData } from './tasksTypes'
import { Task } from "../../../models"
import { FetchErrorInfo, SuccessFetchingAction, StartFetchingAction, ErrorFetchingAction, FetchRequestActions } from '../../../utils/request-actions-helpers/createRequestActions'
import { getDocumentTasksActionTypes, GetTasksForDocumentAdditionalData, createTaskActionTypes, CreateTaskAdditionalData, changeTaskTextActionTypes, DeleteTaskAdditionalData, deleteTaskActionTypes, changeTaskTimeActionTypes, addTaskAttachmentsActionTypes } from './actions'
import { ADD_TASKS, AddTasksAction, addTasks, setTasksAfterFetch, setTasksAfterResponseAction } from './actions/addTasksAction'

export interface TaskState {
    item: Task
    isUpdating: boolean
    updateError?: FetchErrorInfo
    isDeleting: boolean
    deleteError?: FetchErrorInfo
}

export interface ChecklistState {
    tasks: TaskState[]
    isFetching: boolean
    fetchError?: FetchErrorInfo
    isAdding: boolean
    addError?: FetchErrorInfo
}

export interface CheckListToTasks {
    [chackListUid: string]: ChecklistState | undefined
}

export interface TasksState {
    data: CheckListToTasks
}

export const initialTasksState: TasksState = {
    data: {}
}

type TasksReducerActions = TasksActions | UpdateTaskActions | AddTasksAction | FetchRequestActions<Task[], GetTasksForDocumentAdditionalData>
    | FetchRequestActions<Task[], CreateTaskAdditionalData> | FetchRequestActions<never, DeleteTaskAdditionalData>

export const tasksReducer = (state: TasksState = initialTasksState, action: TasksReducerActions): TasksState => {
    switch (action.type) {
        case createTaskActionTypes.success:
        case ADD_TASKS: {
            const addTasksAction = action as AddTasksAction

            return addTasks(state, addTasksAction.result);
        }
        case getDocumentTasksActionTypes.success: {
            const addTasksAction = action as setTasksAfterResponseAction

            return setTasksAfterFetch(state, addTasksAction.result, addTasksAction?.additionalData?.checklistsUids ?? []);
        }

        case createTaskActionTypes.start: {
            const startAction = action as StartFetchingAction<CreateTaskAdditionalData>
            if (!startAction.additionalData) {
                return state
            }
            const { checklistUid } = startAction.additionalData
            const checklistState = state.data[checklistUid]
            let newChecklistState: ChecklistState
            if (checklistState) {
                newChecklistState = {
                    ...checklistState,
                    isAdding: true
                }
            } else {
                newChecklistState = {
                    isAdding: true,
                    isFetching: false,
                    tasks: []
                }
            }

            return {
                data: {
                    ...state.data,
                    [checklistUid]: newChecklistState
                }
            }
        }

        case createTaskActionTypes.error: {
            const errorAction = action as ErrorFetchingAction<CreateTaskAdditionalData>
            if (!errorAction.additionalData) {
                return state
            }
            const { checklistUid } = errorAction.additionalData
            const checklistState = state.data[checklistUid]
            let newChecklistState: ChecklistState
            if (checklistState) {
                newChecklistState = {
                    ...checklistState,
                    isAdding: false,
                    addError: errorAction.error
                }
            } else {
                newChecklistState = {
                    isAdding: false,
                    isFetching: false,
                    tasks: [],
                    addError: errorAction.error
                }
            }

            return {
                data: {
                    ...state.data,
                    [checklistUid]: newChecklistState
                }
            }
        }

        case getDocumentTasksActionTypes.start: {
            const getTasksAction = action as StartFetchingAction<GetTasksForDocumentAdditionalData>
            if (!getTasksAction.additionalData) {
                return state
            }

            const newState: TasksState = { data: { ...state.data } }
            getTasksAction.additionalData.checklistsUids.forEach(x => {
                const checklistState = newState.data[x]
                if (checklistState) {
                    checklistState.isFetching = true
                    delete checklistState.fetchError
                } else {
                    newState.data[x] = {
                        isFetching: true,
                        isAdding: false,
                        tasks: []
                    }
                }
            })

            return newState
        }

        case getDocumentTasksActionTypes.error: {
            const errorGettingTasksAction = action as ErrorFetchingAction<GetTasksForDocumentAdditionalData>
            if (!errorGettingTasksAction.additionalData) {
                return state
            }

            const newState: TasksState = { data: { ...state.data } }
            errorGettingTasksAction.additionalData.checklistsUids.forEach(x => {
                let checklistState = newState.data[x]
                if (checklistState) {
                    checklistState.isFetching = false
                } else {
                    checklistState = {
                        isAdding: false,
                        isFetching: false,
                        tasks: []
                    }
                }
                checklistState.fetchError = errorGettingTasksAction.error
            })

            return newState
        }

        case changeTaskTimeActionTypes.start:
        case changeTaskTextActionTypes.start:
        case addTaskAttachmentsActionTypes.start:
        case toggleTaskStatusActionTypes.start: {
            const startAction = action as StartFetchingAction<UpdateTaskAdditionalData>
            const additionalData = startAction.additionalData;

            if (!additionalData) {
                return state
            }
            const { checklistUid, taskId } = additionalData
            const checklistState = state.data[checklistUid]
            if (!checklistState) {
                return state
            }

            return {
                ...state,
                data: {
                    ...state.data,
                    [checklistUid]: {
                        ...checklistState,
                        tasks: checklistState.tasks.map(x => x.item.taskId === taskId ? { ...x, isUpdating: true } : x),
                    }
                }
            };
        }
        case changeTaskTimeActionTypes.success:
        case changeTaskTextActionTypes.success:
        case addTaskAttachmentsActionTypes.success:
        case toggleTaskStatusActionTypes.success: {
            const successAction = action as SuccessFetchingAction<Task, UpdateTaskAdditionalData>
            const { result, additionalData } = successAction
            if (!additionalData || !Object.keys(result ?? {}).length || !result.taskId) {
                return state
            }

            const { checklistUid, taskId } = additionalData;
            const checklistState = state.data[checklistUid]

            if (!checklistState) {
                return state;
            }

            return {
                data: {
                    ...state.data,
                    [checklistUid]: {
                        ...checklistState,
                        tasks: checklistState.tasks.map(checklistTask => {
                            if (checklistTask.item.taskId === taskId) {
                                return { ...checklistTask, item: result, isUpdating: false }
                            } else {
                                return checklistTask
                            }
                        })
                    }
                }
            }
        }
        case changeTaskTimeActionTypes.error:
        case changeTaskTextActionTypes.error:
        case addTaskAttachmentsActionTypes.error:
        case toggleTaskStatusActionTypes.error: {
            const errorAction = action as ErrorFetchingAction<UpdateTaskAdditionalData>;
            const additionalData = errorAction.additionalData;

            if (!additionalData) {
                return state
            }
            const { checklistUid, taskId } = additionalData

            const checklistState = state.data[checklistUid]
            if (!checklistState) {
                return state
            }

            const newTasks: TaskState[] = checklistState.tasks.map(x => {
                if (x.item.taskId === taskId) {
                    return {
                        ...x,
                        isUpdating: false,
                        item: x.item,
                        updateError: errorAction.error
                    }
                } else {
                    return x
                }
            })

            return {
                ...state,
                data: {
                    ...state.data,
                    [additionalData.checklistUid]: {
                        ...checklistState,
                        tasks: newTasks
                    }
                }
            };
        }

        case deleteTaskActionTypes.start: {
            const deleteAction = action as StartFetchingAction<DeleteTaskAdditionalData>
            if (!deleteAction.additionalData) {
                return state
            }

            const { checklistUid, taskId } = deleteAction.additionalData
            const checklistState = state.data[checklistUid]
            if (!checklistState) {
                return state
            }

            return {
                data: {
                    ...state.data,
                    [checklistUid]: {
                        ...checklistState,
                        tasks: checklistState.tasks.map(x => x.item.taskId === taskId ? { ...x, isDeleting: true } : x)
                    }
                }
            }
        }

        case deleteTaskActionTypes.success: {
            const deleteAction = action as SuccessFetchingAction<never, DeleteTaskAdditionalData>
            if (!deleteAction.additionalData) {
                return state
            }

            const { checklistUid, taskId } = deleteAction.additionalData
            const checklistState = state.data[checklistUid]
            if (!checklistState) {
                return state
            }

            return {
                data: {
                    ...state.data,
                    [checklistUid]: {
                        ...checklistState,
                        tasks: checklistState.tasks.filter(x => x.item.taskId !== taskId)
                    }
                }
            }
        }

        case deleteTaskActionTypes.error: {
            const deleteAction = action as ErrorFetchingAction<DeleteTaskAdditionalData>
            if (!deleteAction.additionalData) {
                return state
            }

            const { checklistUid, taskId } = deleteAction.additionalData
            const checklistState = state.data[checklistUid]
            if (!checklistState) {
                return state
            }

            return {
                data: {
                    ...state.data,
                    [checklistUid]: {
                        ...checklistState,
                        tasks: checklistState.tasks.map(x => x.item.taskId === taskId ? { ...x, isDeleting: false, deleteError: deleteAction.error } : x)
                    }
                }
            }
        }

        default:
            return state
    }
}